import React, { useContext, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Chip,
  makeStyles,
} from "@material-ui/core";
import { Close as CloseIcon, FileCopy as CopyIcon } from "@material-ui/icons";
import { ViewWinnerContext } from "../../../contexts/ViewWinnerContext";
import { getLang } from "app/feature/constants";
import { addAlert } from "modules/notification";
import { useDispatch } from "react-redux";
import moment from "moment-timezone";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 500,
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #E0E0E0",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  prizeSection: {
    display: "flex",
    gap: 16,
    padding: theme.spacing(2),
    backgroundColor: "#F5F5F5",
    borderRadius: 8,
    marginBottom: theme.spacing(3),
  },
  prizeImage: {
    width: 120,
    height: 120,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #E0E0E0",
  },
  fieldLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    fontWeight: 500,
  },
  fieldValue: {
    fontSize: "1rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  fieldValueBold: {
    fontSize: "1rem",
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  twoColumnRow: {
    display: "flex",
    gap: 32,
    marginBottom: theme.spacing(2),
  },
  columnField: {
    flex: 1,
  },
  addressField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  copyIcon: {
    marginLeft: theme.spacing(1),
    cursor: "pointer",
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  statusChips: {
    display: "flex",
    gap: 8,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  processedDate: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    textAlign: "right",
  },
  notesField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F5F5F5",
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #E0E0E0",
  },
  saveButton: {
    backgroundColor: "#3A4D54",
    color: "#ffffff",
    textTransform: "uppercase",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    "&:hover": {
      backgroundColor: "#2C3A3F",
    },
  },
  cancelButton: {
    textTransform: "uppercase",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
  },
}));

export default function AdminActionDialogComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    lang,
    activeTab,
    adminDialog,
    isUpdating,
    timezone,
    handleCloseAdminDialog,
    handleSaveAdminAction,
  } = useContext(ViewWinnerContext);

  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState(0);

  useEffect(() => {
    if (adminDialog.winner) {
      setNotes(adminDialog.winner.admin_notes || "");
      setStatus(adminDialog.winner.claim_fulfill_status || 0);
    }
  }, [adminDialog.winner]);

  const handleCopyAddress = () => {
    const addressValue = getAddressValue();
    if (addressValue) {
      navigator.clipboard.writeText(addressValue);
      dispatch(
        addAlert({
          severity: "success",
          message: getLang(lang, "message.success.COPIED_TO_CLIPBOARD"),
        })
      );
    }
  };

  const handleSave = () => {
    if (adminDialog.winner) {
      handleSaveAdminAction(adminDialog.winner.uuid, status, notes);
    }
  };

  if (!adminDialog.winner) return null;

  const winner = adminDialog.winner;

  // Helper function to get form value by field name
  const getFormValue = (fieldName) => {
    const formValue = winner.form_values?.find(
      (fv) => fv.field_name === fieldName
    );
    return formValue?.value || null;
  };

  // Get winner name from form_values
  const getWinnerName = () => {
    const nameValue = getFormValue("name");
    return nameValue || winner.participant?.name || "-";
  };

  // Get winner email from form_values
  const getWinnerEmail = () => {
    const emailValue = getFormValue("email");
    return emailValue || winner.participant?.email || "-";
  };

  // Get winner contact from form_values
  const getWinnerContact = () => {
    const contactValue = getFormValue("contactNumber");
    if (contactValue && typeof contactValue === "object") {
      return `${contactValue.contactCode || "+60"}${
        contactValue.contactNumber || ""
      }`;
    }
    return winner.participant?.contact_no || "-";
  };

  // Get winner address from form_values
  const getAddressValue = () => {
    const addressValue = getFormValue("address");
    return addressValue || winner.participant?.address || null;
  };

  // Get prize type label
  const getPrizeTypeLabel = () => {
    if (winner.prize_type === 1) {
      return "Physical";
    } else if (winner.prize_type === 2) {
      return "Touch 'n Go";
    } else if (winner.prize_type === 4) {
      return "Loyalty Points";
    }
    return "-";
  };

  return (
    <Dialog
      open={adminDialog.open}
      onClose={handleCloseAdminDialog}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography
          variant="h6"
          style={{ fontWeight: "bold", fontSize: "1.125rem" }}
        >
          {getLang(lang, "label.VIEW_EDIT_DETAIL")}
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleCloseAdminDialog}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Prize Information Section */}
        <Box className={classes.prizeSection}>
          {/* Prize Image */}
          {winner.prizeImage && (
            <img
              src={winner.prizeImage}
              alt={winner.prizeName}
              className={classes.prizeImage}
            />
          )}

          {/* Prize Details */}
          <Box style={{ flex: 1 }}>
            <Typography className={classes.fieldLabel}>
              {getLang(lang, "label.PRIZE_NAME")}
            </Typography>
            <Typography className={classes.fieldValueBold}>
              {winner.prizeName || "-"}
            </Typography>

            <Typography className={classes.fieldLabel}>
              {getLang(lang, "label.PRIZE_TYPE")}
            </Typography>
            <Typography className={classes.fieldValue}>
              {getPrizeTypeLabel()}
            </Typography>
          </Box>
        </Box>

        {/* Winner Name */}
        <Box>
          <Typography className={classes.fieldLabel}>
            {getLang(lang, "label.WINNER_NAME")}
          </Typography>
          <Typography className={classes.fieldValueBold}>
            {getWinnerName()}
          </Typography>
        </Box>

        {/* Winner Phone No. and Email in one row */}
        <Box className={classes.twoColumnRow}>
          <Box className={classes.columnField}>
            <Typography className={classes.fieldLabel}>
              {getLang(lang, "label.WINNER_PHONE_NO")}
            </Typography>
            <Typography className={classes.fieldValue}>
              {getWinnerContact()}
            </Typography>
          </Box>
          <Box className={classes.columnField}>
            <Typography className={classes.fieldLabel}>
              {getLang(lang, "label.WINNER_EMAIL")}
            </Typography>
            <Typography className={classes.fieldValue}>
              {getWinnerEmail()}
            </Typography>
          </Box>
        </Box>

        {/* Winner Address */}
        <Box>
          <Typography className={classes.fieldLabel}>
            {getLang(lang, "label.WINNER_ADDRESS") || "Winner Address"}
          </Typography>
          <Box className={classes.addressField}>
            <Typography
              className={classes.fieldValue}
              style={{ marginBottom: 0 }}
            >
              {getAddressValue() || "-"}
            </Typography>
            {getAddressValue() && (
              <CopyIcon
                fontSize="small"
                className={classes.copyIcon}
                onClick={handleCopyAddress}
              />
            )}
          </Box>
        </Box>

        {/* Processed Date - Show only if already processed */}
        {winner.processed_at && (
          <Box>
            <Typography className={classes.fieldLabel}>
              {getLang(lang, "label.PROCESSED_DATE")?.startsWith("label.")
                ? "Processed Date"
                : getLang(lang, "label.PROCESSED_DATE")}
            </Typography>
            <Typography className={classes.fieldValue}>
              {moment
                .utc(winner.processed_at)
                .tz(timezone)
                .format("D MMM YYYY h:mm A")}
            </Typography>
          </Box>
        )}

        {/* Claim Status - Editable (Only in TO PROCESS tab) */}
        {activeTab === 0 && (
          <Box>
            <Typography className={classes.fieldLabel}>
              {getLang(lang, "label.CLAIM_STATUS")}
            </Typography>
            <Box className={classes.statusChips}>
              <Chip
                label={getLang(lang, "label.UNPROCESSED")}
                clickable
                onClick={() => setStatus(0)}
                style={{
                  backgroundColor: status === 0 ? "#FF9800" : "transparent",
                  color: status === 0 ? "#ffffff" : "#FF9800",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: status === 0 ? "none" : "1px solid #FF9800",
                }}
              />
              <Chip
                label={getLang(lang, "label.PROCESSED")}
                clickable
                onClick={() => setStatus(1)}
                style={{
                  backgroundColor: status === 1 ? "#6AAF68" : "transparent",
                  color: status === 1 ? "#ffffff" : "#6AAF68",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: status === 1 ? "none" : "1px solid #6AAF68",
                }}
              />
              <Chip
                label={getLang(lang, "label.REJECTED")}
                clickable
                onClick={() => setStatus(2)}
                style={{
                  backgroundColor: status === 2 ? "#D32F2F" : "transparent",
                  color: status === 2 ? "#ffffff" : "#D32F2F",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: status === 2 ? "none" : "1px solid #D32F2F",
                }}
              />
            </Box>
          </Box>
        )}

        {/* Admin's Notes */}
        <Box>
          <Typography className={classes.fieldLabel}>
            {getLang(lang, "label.ADMIN_NOTES")}
          </Typography>
          <TextField
            className={classes.notesField}
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder={getLang(lang, "placeholder.YOUR_NOTES_HERE")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleCloseAdminDialog}
          disabled={isUpdating}
          className={classes.cancelButton}
        >
          {getLang(lang, "label.CANCEL")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUpdating}
          className={classes.saveButton}
        >
          {getLang(lang, "label.SAVE")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
